import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ticketsService } from '@/features/booking/services/ticketsService';
import { transferService } from '@/features/booking/services/transferService';
import { useAuth } from '@/hooks/useAuth';
import type { GroupedScheduleResponse, GroupedCartResponse } from '@/features/booking/types/bookingTypes';
import type { BookingTransfer } from '@/features/booking/types/transferTypes';

export interface UseProfileTicketsReturn {
    // Upcoming events
    groupedByEventSchedules: GroupedScheduleResponse[];
    // Past events
    groupedByPastEventSchedules: GroupedScheduleResponse[];
    // Carts
    groupedByCarts: GroupedCartResponse[];
    // Transfers
    transfers: BookingTransfer[];
    // Loading & error states
    ticketsLoading: boolean;
    ticketsError: string | null;
    pastEventsLoading: boolean;
    // Tab states
    activeTab: 'by-event' | 'by-cart' | 'by-transfer';
    setActiveTab: (tab: 'by-event' | 'by-cart' | 'by-transfer') => void;
    activeEventTab: 'upcoming' | 'past';
    // Actions
    fetchTickets: () => Promise<void>;
    handleEventTabChange: (tab: 'upcoming' | 'past') => void;
}

export const useProfileTickets = (
    initialTab: 'by-event' | 'by-cart' | 'by-transfer' = 'by-event'
): UseProfileTicketsReturn => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();

    // State for upcoming events
    const [groupedByEventSchedules, setGroupedByEventSchedules] = useState<GroupedScheduleResponse[]>([]);
    // State for past events
    const [groupedByPastEventSchedules, setGroupedByPastEventSchedules] = useState<GroupedScheduleResponse[]>([]);
    // State for carts
    const [groupedByCarts, setGroupedByCarts] = useState<GroupedCartResponse[]>([]);
    // State for transfers
    const [transfers, setTransfers] = useState<BookingTransfer[]>([]);

    // Loading states
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [ticketsError, setTicketsError] = useState<string | null>(null);
    const [pastEventsLoading, setPastEventsLoading] = useState(false);

    // Track whether data has been fetched to prevent duplicate calls
    const ticketsFetchedRef = useRef(false);
    const pastEventsFetchedRef = useRef(false);

    // Tab states
    const [activeTab, setActiveTab] = useState<'by-event' | 'by-cart' | 'by-transfer'>(initialTab);
    const [activeEventTab, setActiveEventTab] = useState<'upcoming' | 'past'>('upcoming');

    // Fetch all tickets (upcoming events first for better UX, then carts and transfers)
    const fetchTickets = useCallback(async () => {
        // Skip if already fetched or currently loading
        if (ticketsFetchedRef.current || ticketsLoading) return;

        // Proactive check: Skip API call if not authenticated
        if (!authLoading && !isAuthenticated) {
            setTicketsLoading(false);
            setTicketsError('Please log in to view your tickets');
            navigate('/login');
            return;
        }

        // Wait for auth check to complete
        if (authLoading) return;

        setTicketsLoading(true);
        setTicketsError(null);
        ticketsFetchedRef.current = true;

        // Start all 3 API calls simultaneously
        const futureEventsPromise = ticketsService.getAllTicketsGroupedByEventSchedules();
        const cartsPromise = ticketsService.getAllTicketsGroupedByCarts();
        const transfersPromise = transferService.getAllTransfers();

        // Handle future events first (default tab) - show UI as soon as this completes
        futureEventsPromise
            .then(eventSchedulesData => {
                setGroupedByEventSchedules(eventSchedulesData);
                setTicketsLoading(false); // Show UI immediately
            })
            .catch(err => {
                console.error('Error fetching future events:', err);
                setTicketsError('Failed to load tickets. Please try again.');
                toast.error('Failed to load tickets. Please try again.');
                setTicketsLoading(false);
            });

        // Handle carts in background
        cartsPromise
            .then(cartsData => {
                setGroupedByCarts(cartsData);
            })
            .catch(err => {
                console.error('Error fetching carts:', err);
            });

        // Handle transfers in background
        transfersPromise
            .then(transfersData => {
                setTransfers(transfersData);
            })
            .catch(err => {
                console.error('Error fetching transfers:', err);
            });

    }, [authLoading, isAuthenticated, navigate, ticketsLoading]);

    // Fetch past events (lazy loading)
    const fetchPastEvents = useCallback(async () => {
        if (pastEventsFetchedRef.current || pastEventsLoading) return;

        try {
            setPastEventsLoading(true);
            const pastData = await ticketsService.getAllTicketsGroupedByPastEventSchedules();
            setGroupedByPastEventSchedules(pastData);
            pastEventsFetchedRef.current = true;
        } catch (err) {
            console.error('Error fetching past events:', err);
            toast.error('Failed to load past events.');
        } finally {
            setPastEventsLoading(false);
        }
    }, [pastEventsLoading]);

    // Handle nested event tab change
    const handleEventTabChange = useCallback((tab: 'upcoming' | 'past') => {
        setActiveEventTab(tab);
        if (tab === 'past' && !pastEventsFetchedRef.current) {
            fetchPastEvents();
        }
    }, [fetchPastEvents]);

    return {
        groupedByEventSchedules,
        groupedByPastEventSchedules,
        groupedByCarts,
        transfers,
        ticketsLoading,
        ticketsError,
        pastEventsLoading,
        activeTab,
        setActiveTab,
        activeEventTab,
        fetchTickets,
        handleEventTabChange,
    };
};
